import { useEffect } from 'react'
import clsx from 'clsx'
import { useLocation, useNavigate } from 'react-router-dom'

import { APP_ROUTES } from '@/common/constants'
import useIsMobile from '@/common/hooks/useIsMobile'
import { AccountType } from '@/common/types'
import { NAVIGATION_COPY } from '@/locales/en'
import useAccountStore from '@/account/accountStore'
import { NavigationMenuItemClickedAnalytics } from '@/common/analytics/events/NavigationMenuItemClickedAnalytics'
import { getMobileMoreNavItems } from './mobileNavConfig'
import useAccountSwitcher from '@/account/useAccountSwitcher'
import { useFeatureFlags } from '../featureFlags/featureFlags'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'

export function MobileMorePage() {
    const navigate = useNavigate()
    const featureFlags = useFeatureFlags()
    const { pathname } = useLocation()
    const isMobile = useIsMobile()
    const switchAccount = useAccountSwitcher()

    const primaryAccount = usePrimaryAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)

    const isAdviserUser = primaryAccount?.accountType === AccountType.ADVISER
    const items = getMobileMoreNavItems(isAdviserUser, featureFlags)

    useEffect(() => {
        if (!isMobile) {
            navigate(APP_ROUTES.DASHBOARD, { replace: true })
        }
    }, [isMobile, navigate])

    if (!isMobile) {
        return null
    }

    const handleItemClick = async (to: string, key: string) => {
        NavigationMenuItemClickedAnalytics.track({
            menu_name: key,
            user_type: 'customer',
        })

        if (
            to === APP_ROUTES.CLIENTS &&
            isAdviserUser &&
            primaryAccount?.accountId &&
            primaryAccount.accountId !== activeAccountId
        ) {
            navigate(to)
            await switchAccount(primaryAccount.accountId)
            return
        }

        navigate(to)
    }

    return (
        <div className="self-stretch inline-flex flex-col justify-start items-start gap-8">
            <div className="px-3 inline-flex justify-center items-center gap-2.5">
                <h1 className="justify-start text-black text-4xl font-normal font-['Tangerine'] leading-10">
                    {NAVIGATION_COPY.mobileMoreNav.title}
                </h1>
            </div>

            <div className="self-stretch flex flex-col justify-start items-start">
                {items.map((item, index) => {
                    const Icon = item.icon
                    const active = item.isActive(pathname)

                    return (
                        <div key={item.key} className="self-stretch">
                            <button
                                type="button"
                                onClick={() => void handleItemClick(item.to, item.key)}
                                className={clsx(
                                    'self-stretch w-full px-3 py-4 rounded-lg inline-flex justify-start items-center gap-6 text-left',
                                    {
                                        'bg-secondary-light': active,
                                    },
                                )}
                            >
                                <Icon size={24} strokeWidth={1.75} className="text-content-primary shrink-0" />
                                <div className="inline-flex flex-col justify-center items-start gap-2">
                                    <div className="inline-flex justify-start items-end gap-2">
                                        <div className="text-content-primary text-base font-medium font-['Inter'] leading-6">
                                            {item.label}
                                        </div>
                                    </div>
                                    <div className="inline-flex justify-start items-end gap-2">
                                        <div className="text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                                            {item.description}
                                        </div>
                                    </div>
                                </div>
                            </button>
                            {index < items.length - 1 && (
                                <div className="self-stretch h-0 border-t border-border-divider" />
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
